import { useState, useEffect, FormEvent } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  faCircleXmark,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { InputLabel, MainCard, Message } from '@/components';
import { UserCredentials, sendUsernameAndPassword } from '@/services/login';
import { terminosAceptadosWeb } from '@/services/terminosAceptadosWeb';
import { useAuthContext, useQueryParams } from '@/hooks';
import { isAuthenticated } from '@/utils/auth';
import { NavigationSource, PagesPaths } from '@/enums';
import { useMutation } from '@tanstack/react-query';
import { User } from '@/types/auth';
import { Dialog } from 'primereact/dialog';
import { ingresarFechaAprobacion } from '@/services/ingresarFechaAprobacion';
import styles from './Login.module.scss';

const Login = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(PagesPaths.PrivateHome, { replace: true });
    }
  }, [navigate]);

  const [values, setValues] = useState({ username: '', password: '' });
  const [error, setError] = useState({
    username: false,
    password: false,
    alert: false,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [showTerminosModal, setShowTerminosModal] = useState(false);
  const [usuarioNombre, setUsuarioNombre] = useState('');
  const [loginData, setLoginData] = useState<{
    username: string;
    password: string;
  } | null>(null);

  const { t } = useTranslation('login');

  const { onLogin } = useAuthContext();

  const { get: getQueryParam } = useQueryParams();
  const navigatedFrom = getQueryParam('from');
  const userSuccessfullyActivated =
    navigatedFrom === NavigationSource.UserActivation;
  const passwordSuccessfullyChanged =
    navigatedFrom === NavigationSource.ResetPassword;

  const loginMutation = useMutation<User, Error, UserCredentials>({
    mutationFn: sendUsernameAndPassword,
    onSuccess: async (data: User, variables) => {
      try {
        const resp = await terminosAceptadosWeb({
          userName: variables.username,
          password: variables.password,
        });
        if (data && data.accessToken) {
          const { decodeJWT } = await import('@/utils/decodeJWT');
          const decoded = decodeJWT(data.accessToken);
          setUsuarioNombre(decoded.name);
        }
        if (resp === 'NUEVO') {
          setLoginData({
            username: variables.username,
            password: variables.password,
          });
          setShowTerminosModal(true);
          return; // No continuar con el login hasta aceptar
        }
      } catch (err) {
        // No catch
      }
      onLogin(data);
    },
    onError: (loginError) => {
      setErrorMessage(loginError.message);
      setError({
        username: false,
        password: false,
        alert: true,
      });
    },
  });

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();

    let errMessage;
    if (!values.password || !values.username) {
      setError({
        username: !values.username,
        password: !values.password,
        alert: false,
      });
      errMessage = t('errors.required');
      setErrorMessage(errMessage);
      return;
    }
    setError({ username: false, password: false, alert: false });
    setErrorMessage('');

    const { username, password } = values;

    loginMutation.mutate({ username, password });
  };

  const handleChange = (event: FormEvent<HTMLInputElement>) => {
    const { name } = event.currentTarget;
    const { value } = event.currentTarget;
    setValues({ ...values, [name]: value });
    setError((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const submitButtonText = loginMutation.isPending
    ? t('loadingButton')
    : t('loginButton');

  return (
    <MainCard>
      <p className={styles.title}>{t('title')}</p>

      {error.alert && (
        <Message messageText={errorMessage} icon={faCircleXmark} type="error" />
      )}
      {userSuccessfullyActivated && (
        <Message
          icon={faCircleCheck}
          title={t('userActivatedTitle')}
          messageText={t('userActivatedText')}
        />
      )}
      {passwordSuccessfullyChanged && (
        <Message
          icon={faCircleCheck}
          title={t('passwordChangedTitle')}
          messageText={t('passwordChangedText')}
        />
      )}

      <form onSubmit={handleSubmit}>
        <div className={styles.field}>
          <InputLabel htmlFor="username" label={t('username')} />
          <InputText
            name="username"
            onChange={(e) => handleChange(e)}
            className={classNames(error.username && styles.invalid)}
            placeholder={`${t('username')}...`}
          />
          {error.username ? (
            <p className={styles.inputError}>{errorMessage}</p>
          ) : null}
        </div>
        <div className={styles.field}>
          <InputLabel htmlFor="password" label={t('password')} />
          <div
            className={classNames(
              styles.passwordField,
              error.password && styles.invalid
            )}
          >
            <InputText
              name="password"
              onChange={(e) => handleChange(e)}
              placeholder={`${t('password')}...`}
              type={!visiblePassword ? 'password' : 'text'}
              id="current-password"
              autoComplete="current-password"
            />
            <Button
              type="button"
              icon={!visiblePassword ? 'pi pi-eye' : 'pi pi-eye-slash'}
              onClick={() => setVisiblePassword(!visiblePassword)}
              className={styles.eyeButton}
            />
          </div>
          {error.password ? (
            <p className={styles.inputError}>{errorMessage}</p>
          ) : null}
        </div>

        <div className={styles.forgotPassword}>
          <Link to={PagesPaths.ForgotPassword}>{t('forgotPassword')}</Link>
        </div>

        <Button
          type="submit"
          className={styles.button}
          disabled={loginMutation.isPending}
        >
          {submitButtonText}
        </Button>
      </form>
      <Divider />
      <p className={styles.notRegistered}>{t('notRegistered')}</p>
      <Link
        to={PagesPaths.SignUp}
        className={classNames(styles.button, styles.secondaryButton)}
      >
        {t('registerButton')}
      </Link>

      <Dialog
        header="Términos y Condiciones de uso"
        visible={showTerminosModal}
        style={{
          width: '60vw',
          maxWidth: 800,
        }}
        onHide={() => setShowTerminosModal(false)}
        closable={false}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              label="Aceptar"
              onClick={async () => {
                if (loginData) {
                  try {
                    await ingresarFechaAprobacion({
                      userName: loginData.username,
                      password: loginData.password,
                    });
                  } catch (e) {
                    // Manejo de error opcional
                  }
                }
                setShowTerminosModal(false);
                if (loginMutation.data) {
                  onLogin(loginMutation.data);
                }
              }}
              autoFocus
            />
            <Button
              label="Cancelar"
              className="p-button-text"
              onClick={() => setShowTerminosModal(false)}
            />
          </div>
        }
      >
        <div
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            whiteSpace: 'pre-line',
          }}
        >
          {`
CONDICIONES DE USO del SERVICIO CONSULTAS ON LINE de MONTECON S.A.

Señores,  Atención al Cliente
Presente.

Por medio de la presente, `}
          <b>{usuarioNombre}</b>
          {` (en adelante “solicitante” ó “USUARIO”), solicita registrarse como usuario del servicio “consultas on line” ofrecido en la página web "." 

En virtud de la presente solicitud, el USUARIO reconoce y acepta expresamente y sin reservas, las condiciones bajo las cuales prestará el servicio de información “consultas on line”, las que se desarrollan a continuación.

1. GENERALIDADES

Por el sólo hecho de acceder a la página web el USUARIO acepta que:
A. La información publicada en la página web pertenece en exclusividad a MONTECON, pudiendo ésta en cualquier momento y sin previo aviso, modificar, actualizar y/o restringir la información contenida en la página web, en función de los datos recabados por MONTECON.
B. MONTECON, ofrece en su página web el servicio denominado “consultas on line”. Para tener acceso al mismo es necesario estar registrado como USUARIO.
C. El USUARIO se desempeña en la empresa será el único responsable por la correcta utilización de la página web y del servicio “consultas on line”.

2. USUARIOS

2.1 Calidad de usuario. Alta y baja del sistema.
Para convertirse en USUARIO registrado del servicio “consultas on line”, se deberá completar y enviar a MONTECON, la presente solicitud firmada.
MONTECON, resolverá, a su sólo arbitrio aprobar la misma, y en caso de aceptarla, procederá a asignar y a activar un nombre de usuario y password para el solicitante. La activación de dicho nombre usuario y password, será considerada como la aceptación por parte de MONTECON, de la presente solicitud.

Para dar de baja del sistema a un USUARIO, la Empresa por medio de la cual solicitó el alta en el sistema, deberá solicitarlo en forma expresa a MONTECON, indicando el nombre del usuario y el password del mismo. MONTECON, no se responsabiliza por la omisión de la Empresa en solicitar la baja de un USUARIO y el uso que éste pueda darle una vez desvinculado de la misma.

2.2 Nombre de Usuario y Password.
El nombre de usuario y el password, serán únicos y personales para cada solicitante, no habiendo dos nombres de usuarios iguales así como tampoco dos passwords similares. El USUARIO deberá conservar su nombre de usuario y password asignados, en secreto bajo su entera y exclusiva responsabilidad.
El USUARIO no podrá modificar el nombre de usuario asignado por MONTECON. El USUARIO podrá modificar el password asignado por MONTECON.

2.3 Forma de conectarse. Utilización del sistema.
El USUARIO se interconectará con la página web y accederá al sistema MONTECON “on line”, ingresando el nombre de usuario y password informados por MONTECON.
Toda vez que se ingrese al sistema, utilizando un nombre de usuario y password, se entenderá que lo ha efectuado el USUARIO en forma personal, no siendo necesario que MONTECON efectúe control alguno al respecto.
Ni MONTECON, ni sus directores, empleados y personal autorizado, serán responsables por el uso que sujetos no autorizados puedan hacer del nombre de usuario y password, o por la alteración, robo o destrucción de información contenida en la página web o en el sistema MONTECON "on line”, que éstos puedan realizar.

3. RESPONSABILIDAD

El USUARIO es responsable por el uso de este servicio, obligándose a utilizar la página web y el servicio “consultas on line”, siempre de acuerdo con el destino natural de las mismas y comprometiéndose expresamente a evitar cualquier tipo de acción que pueda dañar los sistemas, equipos o servicios que sean accesibles directa o indirectamente a través de internet.
El USUARIO asume plena responsabilidad frente a MONTECON y/o terceros por los daños y perjuicios que se produjeran como consecuencia del accionar de aquellas personas que utilicen su usuario y password, debiendo el USUARIO indemnizar y mantener indemne a MONTECON de cualquier reclamo que se pudiera interponer.
MONTECON empleará sus mayores esfuerzos, para asegurarse que la información contenida en la página web es fiable. Sin perjuicio de lo anterior, en ningún caso MONTECON y/o sus directores, empleados y personal autorizado serán responsables por los daños y perjuicios derivados de la utilización por parte del USUARIO de la información contenida en la página web, así como por los daños y perjuicios de cualquier índole, que pudieran surgir de la inexactitud u omisiones en la información contenida en la página web, o de virus informáticos o de fallos operativos.

4. INTERRUPCIONES O MAL USO DEL SERVICIO

Ni MONTECON ni sus directores, empleados y personal autorizado, serán responsables por el mal uso del servicio por parte del USUARIO, así como de las suspensiones que se originen en el servicio como consecuencias de cortes de energía eléctrica, telecomunicaciones u otra transmisión o servicio público o tecnológico; cualquier falla o funcionamiento deficiente de un equipo, hardware o software, actualización o modificación, o almacenamiento o reubicación de equipo; y cualquier otra causa natural, tecnológica, política o económica u otra fuera de control de MONTECON.
MONTECON no garantiza la disponibilidad y continuidad de la página web, reconociendo y aceptando el USUARIO que MONTECON estará habilitada para suspender definitivamente la prestación del servicio “consultas on line” en cualquier momento, sin necesidad de expresión de causa alguna, y sin que le sea exigible a MONTECON una notificación previa al USUARIO. Ni MONTECON ni sus directores, empleados y personal autorizado serán responsables por ningún daño o pérdida de cualquier naturaleza que pueda ser causado debido a la falta de continuidad de la página web y del servicio “consultas on line”.

5. PROPIEDAD INTELECTUAL

Los derechos de propiedad intelectual y derechos de explotación y reproducción de la página web y del servicio “consultas on line”, incluyendo su apariencia y diseño, los logotipos e información contenida en la misma, son propiedad exclusiva de MONTECON. Cualquier uso indebido de las mismas por persona diferente de su legítimo titular podrá ser perseguido de conformidad con la legislación vigente.

Se acepta y se da conformidad con las condiciones precedentes para obtener la calidad de USUARIO y para la prestación del servicio “consultas on line”.`}
        </div>
      </Dialog>
    </MainCard>
  );
};

export default Login;
